import { acceptHMRUpdate, defineStore } from "pinia";
import { ref, computed } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { type Token, type UserData, type UserCredentials } from "@/models/users";
import { type sfTokenResponse } from "@/models/psCustomerServer";

// {"email":"selsokka@capsuletech.com","st_fullname":"Sameh Elsokkary","st_username":"selsokka","userType":1}
// type User = {
//     email: string,
//     fullName: string,
//     userName: string,
//     userType: string
// }
// export type UserData = User

export interface PsEstimationType {
  token: string | undefined;
  active: boolean;
  exp: number;
  iat: number;
}

export const useUserSession = defineStore("userSession", () => {
  // token will be synced with local storage
  // @see https://vueuse.org/core/usestorage/
  let token = useLocalStorage("token", "");
  // let psSfToken = useLocalStorage('psSfToken', '')

  const psSfToken = useLocalStorage("sfdetails", {
    token: "",
    active: false,
    exp: 0,
    iat: 0,
  });

  const psEstimationSfToken = useLocalStorage("psEstimationSfDetails", {
    token: "",
    active: false,
    exp: 0,
    iat: 0,
  });

  const activeBreadcrumb = useLocalStorage("activeBreadcrumb",{
    name: ''
  })

  const user = ref<UserData>(
    useLocalStorage("vueuseuser", {
      email: "",
      fullName: "",
      userName: "",
      userType: "",
    }) as any
  );

  const loading = ref(true);

  const isLoggedIn = computed(() => token.value != "");

  const getPsCustomerServerTokenDetails = computed(() => psSfToken.value);

  const getPsEstimationTokenDetails = computed(() => psEstimationSfToken.value);

  const getActiveBreadcrumb = computed(() => activeBreadcrumb.value.name);

  const isSalesForceLoggedIn = computed(
    () => psSfToken.value.token != "" && psSfToken.value.active
  );

  // Unlike isSalesForceLoggedIn (which trusts the "active" flag captured at the
  // last login/refresh), this checks the token's actual exp timestamp so callers
  // can detect a token that has since expired and trigger a refresh.
  const isSalesForceTokenExpired = computed(() => {
    const details = psSfToken.value;
    if (!details.token || !details.active) return true;
    if (!details.exp) return false;
    return Math.floor(Date.now() / 1000) >= details.exp;
  });

  // Also honours the token's exp (unix seconds, same as isSalesForceTokenExpired),
  // so an expired PS-estimation SF session is treated as logged out.
  const isPsEstimationSalesForceLoggedIn = computed(() => {
    const details = psEstimationSfToken.value;
    if (details.token == "" || !details.active) return false;
    if (!details.exp) return true;
    return Math.floor(Date.now() / 1000) < details.exp;
  });

  function setUser(newUser: UserData) {
    user.value = newUser;
  }

  function setToken(newToken: string) {
    token.value = newToken;
  }

  function setPsCustomerAuthDetail(tokenDetails: sfTokenResponse) {
    psSfToken.value.token = tokenDetails.ccrm_access_token || tokenDetails.access_token || "";
    psSfToken.value.active = tokenDetails.active;
    psSfToken.value.exp = tokenDetails.exp;
    psSfToken.value.iat = tokenDetails.iat;
  }

  function setPsEstimationAuthDetail(tokenDetails: sfTokenResponse) {
    psEstimationSfToken.value.token = tokenDetails.ccrm_access_token || tokenDetails.access_token || "";
    psEstimationSfToken.value.active = tokenDetails.active;
    psEstimationSfToken.value.exp = tokenDetails.exp;
    psEstimationSfToken.value.iat = tokenDetails.iat;
  }

  function setLoading(newLoading: boolean) {
    loading.value = newLoading;
  }

  function setActiveBreadCrumb(newBreadcrumb: string) {
    activeBreadcrumb.value.name = newBreadcrumb
  }

  async function logoutUser() {
    psSfToken.value = {
      token: "",
      active: false,
      exp: 0,
      iat: 0,
    };
    psEstimationSfToken.value = {
      token: "",
      active: false,
      exp: 0,
      iat: 0,
    };
    token.value = "";
    user.value = {
      email: "",
      fullName: "",
      userName: "",
      userType: "",
    };
  }

  async function login(data: UserData & Token) {
    const userDetail = {
      email: data.email,
      fullName: data.fullName,
      userName: data.userName,
      userType: data.userType,
    };
    // store user details and jwt in local storage to keep user logged in between page refreshes
    setToken(data.token);
    token.value = data.token;
    setUser(userDetail);
    // redirect to previous url or default to home page
    // router.push(this.returnUrl || '/');
  }

  return {
    user,
    psSfToken,
    psEstimationSfToken,
    token,
    isLoggedIn,
    loading,
    isSalesForceLoggedIn,
    isSalesForceTokenExpired,
    isPsEstimationSalesForceLoggedIn,
    getPsCustomerServerTokenDetails,
    getPsEstimationTokenDetails,
    getActiveBreadcrumb,
    logoutUser,
    setUser,
    setToken,
    setLoading,
    login,
    setPsCustomerAuthDetail,
    setPsEstimationAuthDetail,
    setActiveBreadCrumb,
  } as const;
});

/**
 * Pinia supports Hot Module replacement so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserSession, import.meta.hot));
}
